import { createContext, use, useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";

import * as proxy from "../../../services/api/proxy";
import { useNewSveaBankIdAuthFlow } from "./useNewSveaBankIdAuthFlow";

import type { Middleware } from "../../../services/api/client/AppMiddleware";
import type {
	DelegateInformation as AuthFlowDelegateInformation,
	NewSveaBankIdAuthFlowResult,
} from "./useNewSveaBankIdAuthFlow";
import type { FC, PropsWithChildren } from "react";

/*
    This hook provides Svea BankID authentication functionality.

    To setup;
        * call useNewSveaAuthenticationProvider to get the ContextProvider and middleware.
        * register the middleware onto your fetch pipeline (appMiddleware).
        * Wrap your app in the ContextProvider.


    To use: call useNewSveaAuthentication to get access to the authenticate() function.


    The middleware intercepts 307 responses designated for svea authentication. (307 with "interactionRequest" header set to "RequireAuthentication").
    When such a response is detected, it triggers the Svea BankID authentication flow via a modal dialog.
    If the user successfully authenticates, the original request is retried.
    If the user cancels or fails authentication, a standardized error response is returned. (This is to be re-evaluated!)


    The middleware is capable of handling multiple requests at once; should multiple requests trigger authentication, all requests will be resolved once the user completes the authentication flow.

    In essence - as long as ANY request is stuck waiting for Svea authentication to complete, the modal will appear, requiring authentication.

    TODO: This may cause an infinite loop if we fail to authenticate the user (svea is down or malfunctionional, or we encounter any issues in BE).
    So we need a way to disable the auth flow, on the occasions where we simply give up.

*/

export type DelegateInformation = AuthFlowDelegateInformation;

type AuthenticationResult =
	| {
			status: "succeeded";
			bearerToken: string;
			refreshToken: string;
			delegateInformation: DelegateInformation;
	  }
	| {
			status: "cancelled";
	  };

//Represents the current authentication state of the user, including any delegate information if authenticated.
export type AuthenticationState =
	| { type: "authenticated"; delegateInformation: DelegateInformation }
	| { type: "unauthenticated" };

interface NewSveaAuthenticationContextValue {
	useState: () => AuthenticationState;

	// Cancels the pending refresh timer and immediately triggers a token refresh.
	// The refresh loop continues afterwards as normal.
	refreshToken: () => void;

	// Cancels the refresh loop and sets the state to "unauthenticated". Safe to call at any time.
	logout: () => void;

	// Bypasses the BankID flow; only use in development or Storybook.
	debugSetState: (state: AuthenticationState) => void;

	// Opens the BankID modal and returns a promise that resolves once the flow completes.
	authenticate: () => Promise<AuthenticationResult>;
}

export const NewSveaAuthenticationContext = createContext<NewSveaAuthenticationContextValue>({
	// eslint-disable-next-line @eslint-react/no-unnecessary-use-prefix
	useState: () => ({ type: "unauthenticated" }),
	authenticate: async (): Promise<AuthenticationResult> => {
		throw new Error("useNewSveaAuthentication must be used within a NewSveaAuthenticationProvider");
	},
	debugSetState: () => {
		/* empty */
	},
	refreshToken: () => {
		/* empty */
	},
	logout: () => {
		/* empty */
	},
});

export const useNewSveaAuthentication = () => use(NewSveaAuthenticationContext);

const SVEA_AUTH_STATE_STORAGE_KEY = "svea_auth_state_v1";

type PersistedAuthState = {
	state: AuthenticationState;
	expiresAt: number;
};

function readPersistedAuthState(): PersistedAuthState | undefined {
	try {
		const raw = sessionStorage.getItem(SVEA_AUTH_STATE_STORAGE_KEY);
		if (!raw) {
			return undefined;
		}
		const parsed = JSON.parse(raw) as PersistedAuthState;
		if (Date.now() > parsed.expiresAt) {
			sessionStorage.removeItem(SVEA_AUTH_STATE_STORAGE_KEY);
			return undefined;
		}
		return parsed;
	} catch {
		return undefined;
	}
}

function writePersistedAuthState(state: AuthenticationState, lifetimeMs: number): void {
	try {
		if (state.type === "authenticated") {
			const persisted: PersistedAuthState = {
				state,
				expiresAt: Date.now() + lifetimeMs,
			};
			sessionStorage.setItem(SVEA_AUTH_STATE_STORAGE_KEY, JSON.stringify(persisted));
		} else {
			sessionStorage.removeItem(SVEA_AUTH_STATE_STORAGE_KEY);
		}
	} catch {
		/* ignore storage errors */
	}
}

const AuthModalWrapper: FC<{
	AuthModal: FC<NewSveaBankIdAuthFlowResult>;
	onSuccess: (bearerToken: string, refreshToken: string, delegateInformation: DelegateInformation) => void;
	onCancel: () => void;
}> = ({ AuthModal, onSuccess, onCancel }) => {
	const authFlow = useNewSveaBankIdAuthFlow();

	useEffect(() => {
		if (authFlow.state.type === "Completed") {
			onSuccess(authFlow.state.bearerToken, authFlow.state.refreshToken, authFlow.state.delegateInformation);
		} else if (authFlow.state.type === "Cancelling" || authFlow.state.type === "Cancelled") {
			onCancel();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [authFlow.state.type, onCancel, onSuccess]);

	return <AuthModal {...authFlow} />;
};

interface NewSveaAuthenticationProvider {
	ContextProvider: FC<PropsWithChildren>;
	AuthenticationModal: FC;
	middleware: Middleware;
}

export const useNewSveaAuthenticationProvider = ({
	AuthModal,
}: {
	AuthModal: FC<NewSveaBankIdAuthFlowResult>;
}): NewSveaAuthenticationProvider => {
	const initialPersistedStateRef = useRef(readPersistedAuthState());
	const stateRef = useRef<AuthenticationState>(initialPersistedStateRef.current?.state ?? { type: "unauthenticated" });
	const subscribersRef = useRef<Set<() => void>>(new Set());
	const subscribeToAuthState = useCallback((callback: () => void) => {
		subscribersRef.current.add(callback);
		return () => {
			subscribersRef.current.delete(callback);
		};
	}, []);
	const setAuthStateInternal = useCallback((newState: AuthenticationState, lifetimeMs: number) => {
		stateRef.current = newState;
		writePersistedAuthState(newState, lifetimeMs);
		subscribersRef.current.forEach(callback => callback());
	}, []);

	const getStateSnapshot = useCallback(() => stateRef.current, []);
	const useAuthState = useCallback(
		// Not sure this works as intended, this breaks the rules of hooks. disabling to get linting to pass.
		// eslint-disable-next-line react-hooks/rules-of-hooks
		() => useSyncExternalStore(subscribeToAuthState, getStateSnapshot, getStateSnapshot),
		[subscribeToAuthState, getStateSnapshot],
	);
	const [isAuthenticating, setIsAuthenticating] = useState(false);
	// Multiple callers may trigger authenticate() simultaneously (e.g. parallel API requests all getting a 307).
	// All their promise resolvers are queued here and resolved together once the single modal flow completes.
	const pendingPromiseResolversRef = useRef<((response: AuthenticationResult) => void)[]>([]);
	const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	// Holds the current async refresh function. Setting this to undefined is the signal to stop the loop â€”
	// logout() clears it so any in-flight refresh request that resolves after logout becomes a no-op.
	const doRefreshRef = useRef<(() => Promise<void>) | undefined>(undefined);

	// Schedules the refresh at the earlier of (JWT exp - 60s) or 4 minutes.
	// The svea-session cookie has a 5-minute lifetime â€” always refresh before it expires
	// regardless of how long the Svea bearer token JWT itself is valid for.
	// Minimum is 30 seconds to avoid hammering.
	const getMsUntilRefresh = useCallback((token: string): number => {
		const cookieDeadlineMs = 4 * 60 * 1000; // refresh 1 min before the 5-min cookie expires
		try {
			const payload = JSON.parse(atob(token.split(".")[1])) as { exp: number };
			const msUntilJwtExpiry = payload.exp * 1000 - Date.now() - 60_000;
			return Math.max(Math.min(msUntilJwtExpiry, cookieDeadlineMs), 30_000);
		} catch {
			return cookieDeadlineMs;
		}
	}, []);

	const stopTokenRefresh = useCallback(() => {
		if (refreshTimeoutRef.current) {
			clearTimeout(refreshTimeoutRef.current);
			refreshTimeoutRef.current = null;
		}
		doRefreshRef.current = undefined;
	}, []);

	// Starts (or restarts) the token refresh loop.
	// The refresh call intentionally bypasses the Svea auth middleware (uses window.fetch directly)
	// so that an expired session never triggers the BankID modal in the background.
	// On session expiry: stop the loop and reset auth state â€” the user will be prompted when
	// they next perform a Svea-protected action.
	// On transient network error: retry after 30 seconds without disrupting auth state.
	// The loop is stopped by logout(), which clears doRefreshRef to make any in-flight call a no-op.
	const scheduleTokenRefresh = useCallback(
		(initialDelayMs?: number) => {
			if (refreshTimeoutRef.current) {
				clearTimeout(refreshTimeoutRef.current);
			}
			doRefreshRef.current = async () => {
				try {
					// Use window.fetch directly to bypass the Svea auth middleware.
					// If the session has expired and the backend returns a 307 (RequireAuthentication),
					// we must NOT open the BankID modal automatically â€” that should only happen
					// when the user explicitly triggers a Svea-protected action.
					// redirect: "manual" prevents fetch from silently following a 307 redirect â€”
					// without it the original response/headers are lost and we'd end up in the catch
					// block retrying every 30 s while auth state incorrectly stays "authenticated".
					const raw = await window.fetch("/BankApi/SveaAuth2/RefreshToken", { method: "POST", redirect: "manual" });
					if (!doRefreshRef.current) {
						return; // logged out while the request was in flight
					}
					if (!raw.ok) {
						// 307 (interaction required), 4xx, or 5xx â€” session is gone, stop the loop.
						doRefreshRef.current = undefined;
						setAuthStateInternal({ type: "unauthenticated" }, 0);
						return;
					}
					const result = (await raw.json()) as { Success: boolean; Data?: { BearerToken: string } };
					if (!doRefreshRef.current) {
						return; // logged out while parsing
					}
					if (result.Success && result.Data?.BearerToken) {
						refreshTimeoutRef.current = setTimeout(
							() => void doRefreshRef.current?.(),
							getMsUntilRefresh(result.Data.BearerToken),
						);
					} else {
						// Envelope-level failure â€” session expired, stop the loop.
						doRefreshRef.current = undefined;
						setAuthStateInternal({ type: "unauthenticated" }, 0);
					}
				} catch {
					// Transient network error â€” retry shortly without disrupting auth state.
					if (!doRefreshRef.current) {
						return;
					}
					refreshTimeoutRef.current = setTimeout(() => void doRefreshRef.current?.(), 30_000);
				}
			};
			refreshTimeoutRef.current = setTimeout(() => void doRefreshRef.current?.(), initialDelayMs);
		},
		[getMsUntilRefresh, setAuthStateInternal],
	);

	const setAuthState = useCallback(
		(newState: AuthenticationState, lifetimeMs: number) => {
			setAuthStateInternal(newState, lifetimeMs);

			if (newState.type === "authenticated") {
				scheduleTokenRefresh(lifetimeMs);
			} else {
				stopTokenRefresh();
			}
			subscribersRef.current.forEach(callback => callback());
		},
		[scheduleTokenRefresh, setAuthStateInternal, stopTokenRefresh],
	);

	//This is the initializer to ensure that the refresh loop is correctly started if we have a valid persisted authenticated state on load.
	useEffect(() => {
		const persisted = initialPersistedStateRef.current;
		if (persisted?.state.type !== "authenticated") {
			return;
		}
		const msUntilRefresh = persisted.expiresAt - Date.now();
		if (msUntilRefresh <= 0) {
			setAuthState({ type: "unauthenticated" }, 0);
			return;
		}
		scheduleTokenRefresh(msUntilRefresh);
	}, [scheduleTokenRefresh, setAuthState]);

	const authenticate = useCallback(() => {
		setIsAuthenticating(true);

		return new Promise<AuthenticationResult>(resolve => {
			pendingPromiseResolversRef.current.push(resolve);
		});
	}, [setIsAuthenticating]);

	const onSuccess = useCallback(
		(bearerToken: string, refreshToken: string, delegateInformation: DelegateInformation) => {
			setIsAuthenticating(false);
			const expiryLifetimeMs = getMsUntilRefresh(bearerToken);
			setAuthState({ type: "authenticated", delegateInformation: delegateInformation }, expiryLifetimeMs);
			pendingPromiseResolversRef.current.forEach(resolve =>
				resolve({
					status: "succeeded",
					bearerToken,
					refreshToken,
					delegateInformation: delegateInformation,
				}),
			);
			pendingPromiseResolversRef.current = [];
		},
		[setAuthState, getMsUntilRefresh],
	);

	const onCancel = useCallback(() => {
		setIsAuthenticating(false);
		setAuthState({ type: "unauthenticated" }, 0);
		pendingPromiseResolversRef.current.forEach(resolve => resolve({ status: "cancelled" }));
		pendingPromiseResolversRef.current = [];
	}, [setAuthState]);

	const refreshToken = useCallback(() => {
		if (refreshTimeoutRef.current) {
			clearTimeout(refreshTimeoutRef.current);
			refreshTimeoutRef.current = null;
		}
		void doRefreshRef.current?.();
	}, []);

	const logout = useCallback(() => {
		setAuthState({ type: "unauthenticated" }, 0);
		proxy.Bank.SveaAuth2Controller.Logout.Post().catch(() => {
			/* ignore logout errors */
		});
	}, [setAuthState]);

	const ContextProvider: FC<PropsWithChildren> = useMemo(
		() =>
			({ children }) => (
				<NewSveaAuthenticationContext
					value={{
						useState: useAuthState,
						authenticate,
						debugSetState: state => setAuthState(state, 24 * 60 * 60 * 1000),
						refreshToken,
						logout,
					}}
				>
					{children}
				</NewSveaAuthenticationContext>
			),
		[authenticate, refreshToken, logout, setAuthState, useAuthState],
	);

	const AuthenticationModal: FC = useMemo(
		() => () =>
			isAuthenticating ? <AuthModalWrapper AuthModal={AuthModal} onSuccess={onSuccess} onCancel={onCancel} /> : null,
		[AuthModal, isAuthenticating, onSuccess, onCancel],
	);

	const middleware: Middleware = fetch => async (input, options) => {
		let response = await fetch(input, options);
		while (true) {
			if (response.status === 307 && response.headers.get("interactionRequest") === "RequireAuthentication") {
				console.log("Init authentication flow for", input);
				const authResponse = await authenticate();
				console.log("Authentication flow completed for", input);

				if (authResponse.status === "succeeded") {
					const myOptions = {
						...options,
						headers: {
							...options?.headers,
							interactionRequestId: response.headers.get("interactionRequestRequestId"),
							interactionRequestInteractionID: response.headers.get("interactionRequestInteractionId"),
							interactionRequestInteractionResponse: JSON.stringify({
								Status: "Succeeded",
								BearerToken: authResponse.bearerToken,
								RefreshToken: authResponse.refreshToken,
							}),
						},
					};
					response = await fetch(input, myOptions);
				} else if (authResponse.status === "cancelled") {
					const myOptions = {
						...options,
						headers: {
							...options?.headers,
							interactionRequestId: response.headers.get("interactionRequestRequestId"),
							interactionRequestInteractionID: response.headers.get("interactionRequestInteractionId"),
							interactionRequestInteractionResponse: JSON.stringify({
								Status: "Cancelled",
							}),
						},
					};
					response = await fetch(input, myOptions);
				}
			} else {
				break;
			}
		}

		return response;
	};

	return {
		ContextProvider,
		AuthenticationModal,
		middleware,
	};
};
